import React, { useEffect, useState, useMemo } from "react";
import { Link, useOutletContext } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronRight, Flame, Sparkles, ShieldCheck, Truck, MapPin, ChevronsUpDown } from "lucide-react";
import * as ella from "@/lib/ellaApi";
import { bannerImg, categoryImg } from "@/lib/ellaApi";
import { useStore } from "@/lib/store";
import { useSeo } from "@/lib/seo";
import ProductCard from "@/components/ProductCard";
import PlayStoreBanner from "@/components/PlayStoreBanner";

function BannerCarousel({ banners }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!embla) return;
    const update = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", update);
    const i = setInterval(() => embla.scrollNext(), 4500);
    return () => { embla.off("select", update); clearInterval(i); };
  }, [embla]);

  if (!banners?.length) return <div className="aspect-[2/1] mx-4 mt-4 rounded-2xl skeleton" />;

  return (
    <div className="mt-3 px-4" data-testid="banner-carousel">
      <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
        <div className="flex">
          {banners.map((b, i) => {
            // Banners are authored at 2:1 — match that ratio so the full artwork
            // shows (no crop) and every slide keeps a consistent height.
            const img = <img src={bannerImg(b.ImagePath)} alt={b.Name} className="block w-full aspect-[2/1] object-cover" />;
            // Legacy behavior: a banner Keyword opens the product search,
            // otherwise its URL opens externally.
            if (b.Keyword) {
              return (
                <Link key={b.ID} to={`/search?q=${encodeURIComponent(b.Keyword)}`} className="relative min-w-0 flex-[0_0_100%]" data-testid={`banner-${i}`}>
                  {img}
                </Link>
              );
            }
            return (
              <a
                key={b.ID}
                href={b.URL ? (b.URL.startsWith("http") ? b.URL : `https://${b.URL}`) : undefined}
                target={b.URL ? "_blank" : undefined}
                rel="noreferrer"
                className="relative min-w-0 flex-[0_0_100%]"
                data-testid={`banner-${i}`}
              >
                {img}
              </a>
            );
          })}
        </div>
      </div>
      <div className="flex justify-center gap-1.5 mt-2">
        {banners.map((_, i) => (
          <div key={i} className={`embla__dot ${i === selected ? "embla__dot--selected" : ""}`} />
        ))}
      </div>
    </div>
  );
}

function CategoryGrid({ categories }) {
  return (
    <section className="mt-4 px-4" data-testid="categories-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-base font-bold text-gray-900">Kategori untuk Anda</h2>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <Link to="/category/promo" className="flex flex-col items-center gap-1.5 group" data-testid="category-promo">
          <div className="w-full aspect-square rounded-2xl bg-white flex items-center justify-center">
            <Flame className="w-10 h-10 text-[#D32F2F]" />
          </div>
          <span className="text-[10px] text-center text-gray-700 leading-tight min-h-[24px]">Promo</span>
        </Link>
        {categories.slice(0, 8).map((c) => (
          <Link
            key={c.ID}
            to={`/category/${c.ID}`}
            className="flex flex-col items-center gap-1.5 group"
            data-testid={`category-${c.ID}`}
          >
            <div className="w-full aspect-square rounded-2xl bg-white overflow-hidden flex items-center justify-center">
              <img src={categoryImg(c.ImagePath)} alt={c.Name} className="w-12 h-12 object-contain" />
            </div>
            <span className="text-[10px] text-center text-gray-700 leading-tight line-clamp-2 min-h-[24px]">{c.Name}</span>
          </Link>
        ))}
      </div>
      {categories.length > 8 && (
        <Link to={`/category/all`} className="mt-2 text-xs text-[#D32F2F] font-semibold flex items-center justify-end gap-1">
          Lihat semua kategori <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </section>
  );
}

function HighlightStrip() {
  const items = [
    { icon: ShieldCheck, label: "Halal & Higienis" },
    { icon: Truck, label: "Same Day Delivery" },
    { icon: Sparkles, label: "Fresh Frozen" },
  ];
  return (
    <div className="mt-4 px-4">
      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-3 grid grid-cols-3 gap-2">
        {items.map(({ icon: Icon, label }) => (
          <div key={label} className="flex flex-col items-center gap-1 text-center">
            <Icon className="w-5 h-5 text-[#D32F2F]" />
            <span className="text-[10px] font-medium text-gray-700 leading-tight">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// The whole catalog (products, prices, discounts) is branch-scoped, so make the
// active branch obvious and one tap to change right where the products start.
function BranchBanner({ branch, onChange }) {
  return (
    <div className="mt-5 px-4">
      <button
        onClick={onChange}
        className="w-full flex items-center gap-3 bg-white border border-gray-200 rounded-2xl p-3 text-left hover:border-[#D32F2F] transition"
        data-testid="home-branch-banner"
      >
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
          <MapPin className="w-5 h-5 text-[#D32F2F]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-gray-500 leading-tight">Kamu belanja dari cabang</p>
          <p className="font-display font-bold text-gray-900 leading-tight truncate" data-testid="home-branch-name">
            {branch?.Name || "Pilih Cabang"}
          </p>
          <p className="text-[10px] text-gray-400 leading-tight mt-0.5">Produk, harga & diskon mengikuti cabang ini</p>
        </div>
        <span className="shrink-0 flex items-center gap-1 text-xs font-semibold text-[#D32F2F] bg-red-50 rounded-full px-2.5 py-1.5">
          <ChevronsUpDown className="w-3.5 h-3.5" /> Ganti
        </span>
      </button>
    </div>
  );
}

export default function HomePage() {
  const { branch, banners, categories } = useStore();
  const { openBranch } = useOutletContext() || {};
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: null, // home keeps the full brand title from index.html
    description: `Belanja frozen food halal & higienis${branch ? ` dari cabang ${branch.Name}` : ""}: bakso ikan, olahan tenggiri, surimi, seafood beku. Dikirim same-day.`,
    path: "/",
  });

  useEffect(() => {
    if (!branch) return;
    setLoading(true);
    ella.getAllProducts({ branchId: branch.ID })
      .then((res) => setProducts(res.status ? res.data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [branch]);

  const discountProducts = useMemo(
    () => products.filter((p) => ella.hasDiscount(p.DiscountType)),
    [products]
  );

  return (
    <div className="pb-8">
      <BannerCarousel banners={banners} />
      <PlayStoreBanner />
      <HighlightStrip />
      <CategoryGrid categories={categories} />
      <BranchBanner branch={branch} onChange={() => openBranch?.()} />

      {discountProducts.length > 0 && (
        <section className="mt-6 px-4" data-testid="discount-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-bold text-gray-900 flex items-center gap-1.5">
              <Flame className="w-5 h-5 text-[#D32F2F]" /> Diskon Hari Ini
            </h2>
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2">
            {discountProducts.slice(0, 8).map((p) => (
              <div key={p.ProductID} className="w-40 shrink-0">
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-6 px-4 pb-8" data-testid="all-products-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-base font-bold text-gray-900">Semua Produk</h2>
          <span className="text-xs text-gray-500">{products.length} produk</span>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] skeleton rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {products.map((p) => (
              <ProductCard key={p.ProductID} product={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
